import React, {useEffect, useState} from 'react'
import { InputInline, SelectAsyncInline, SelectInline, TextArea  } from '@arnsbogroup/react-voicearchive'
import debounce from 'debounce-promise'
import useCurrentUser from 'hooks/useCurrentUser'

const DefaultValues = ( {customer, onChange} ) => {
  if (!customer) {
      return null
  }

  const currentUser = useCurrentUser();

  const [sourceLanguage, setSourceLanguage] = useState(null);
  const [targetLanguage, setTargetLanguage] = useState(null);
  const [orderUsage, setOrderUsage] = useState(null);
  const [audioFormat, setAudioFormat] = useState(null);
  const [orderTargetMedia, setOrderTargetMedia] = useState(null);
  const [accountingDebtorGroup, setAccountingDebtorGroup] = useState(null);
  const [economicLayoutHandle, setEconomicLayoutHandle] = useState(null);
  const [economicTermOfPaymentHandle, setEconomicTermOfPaymentHandle] = useState(null);

  useEffect(() => {
    fetchSourceLanguage();
    fetchTargetLanguage();
    fetchOrderUsage();
    fetchAudioFormat();
    fetchOrderTargetMedia();
    fetchDebtorGroup();
    fetchInvoiceLayout();
    fetchTermOfPayment();
  }, [customer]);

  const fetchSourceLanguage = () => {
    if (customer.defaultsourcelang !== '0') {
      fetch(`/rest/v2/language/${customer.defaultsourcelang}`)
        .then((response) => response.json())
        .then((data) => {
          setSourceLanguage(data);
        });
    }
  }
  const fetchTargetLanguage = () => {
    if (customer.defaulttargetlang !== '0') {
      fetch(`/rest/v2/language/${customer.defaulttargetlang}`)
        .then((response) => response.json())
        .then((data) => {
          setTargetLanguage(data);
        });
    }
  }
  const fetchOrderUsage = () => {
    if (customer.defaultusage !== '0') {
      fetch(`/rest/v1/orderusage/${customer.defaultusage}`)
        .then((response) => response.json())
        .then((data) => {
          setOrderUsage(data);
        });
    }
  }
  const fetchAudioFormat = () => {
    if (customer.defaultaudioformat !== '0') {
      fetch(`/rest/v1/audioformat/${customer.defaultaudioformat}`)
        .then((response) => response.json())
        .then((data) => {
          setAudioFormat(data);
        });
    }
  }
  const fetchOrderTargetMedia = () => {
    if (customer.defaultmedia !== '0') {
      fetch(`/rest/v1/ordertargetmedia/${customer.defaultmedia}`)
        .then((response) => response.json())
        .then((data) => {
          setOrderTargetMedia(data);
        });
    }
  }
  const fetchDebtorGroup = () => {
    if (customer.economicDebtorGroupHandle !== '0') {
      fetch(`/rest/v1/accounting_debtor_group/${customer.economicDebtorGroupHandle}`)
        .then((response) => response.json())
        .then((data) => {
          setAccountingDebtorGroup(data);
        });
    }
  }
  const fetchInvoiceLayout = () => {
    if (customer.economicLayoutHandle !== '0') {
      fetch(`/rest/v1/accounting_invoice_layout/${customer.economicLayoutHandle}`)
        .then((response) => response.json())
        .then((data) => {
          setEconomicLayoutHandle(data);
        });
    }
  }
  const fetchTermOfPayment = () => {
    if (customer.economicTermOfPaymentHandle !== '0') {
      fetch(`/rest/v1/accounting_payment_term/${customer.economicTermOfPaymentHandle}`)
        .then((response) => response.json())
        .then((data) => {
          setEconomicTermOfPaymentHandle(data);
        });
    }
  }

  const setDiscountPercentage = (val) => {
    if (isNaN(val)) {
      toastr.error("The discount should be a number!");
      return false;
    }
    const decimal = val.split('.')[1] || 0;
    if (customer.partnerConfig.isPartner) {
        if (val < 0 || val > 100) {
            toastr.error("The discount should be between 0 and 100!");
            return false;
        }
        if (decimal.length > 1) {
            toastr.error("The discount can only have a single decimal!");
            return false;
        }
    } else {
        if (val < 0 || val > 40) {
            toastr.error("This discount should be between 0 and 40!");
            return false;
        }
        if (decimal != 0 && decimal != 5) {
            toastr.error("The discount can only be with 0.5 interval");
            return false;
        }
    }
    
    onChange({ ...customer, discount: val })
  }

  function changePartnerHandler(partnerId) {
    const body = new FormData()
    body.append('customerId', customer.uid)
    body.append('newPartnerId', partnerId)

    fetch(`/customerdisplay/changePartner.json`, {
      method: "POST",
      body: body,
    })
      .then(() => {
        toastr.success("Partner changed successfully");
        onChange(customer, true); // force refresh to fetch new partner
      })
  }
  
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      <div className='p-4 space-y-2'>
        <SelectAsyncInline
          value={(customer.currency?.name) ? { value: customer.currency.uid, label: customer.currency.name } : null}
          onChange={(selection) => {
              onChange({ ...customer, currency: selection.value })
          }}
          options={[]}
          label="Currency"
          placeholder="Currency"
          noOptionsMessage={({ inputValue }) => (inputValue) ? `No currency found for '${inputValue}'` : 'No currency found'}
          loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
          cacheOptions
          defaultOptions
          loadOptions={
              debounce((query) => fetch(`/rest/v1/currency?search[name]=${query}`)
              .then((response) => response.json())
              .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
              , 250)
          }
        />

        <InputInline
          labelClassName="text-xs"
          label="Discount percentage (VO Only)"
          placeholder="Discount percentage"
          value={ customer.discount }
          onChange={({ target: { value } }) => {
            setDiscountPercentage(value)
          }}
        />

        <SelectAsyncInline
          value={(sourceLanguage) ? { value: sourceLanguage.uid, label: sourceLanguage.name } : null}
          onChange={(selection) => {
              onChange({ ...customer, defaultsourcelang: (selection) ? selection.value : '' })
          }}
          options={[]}
          label="Default source language"
          placeholder="Default source language"
          noOptionsMessage={({ inputValue }) => (inputValue) ? `No language found for '${inputValue}'` : 'No language found'}
          loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
          cacheOptions
          defaultOptions
          isClearable
          loadOptions={
              debounce((query) => fetch(`/rest/v2/language?search[name]=${query}`)
                .then((response) => response.json())
                .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
              , 250)
          }
        />

        <SelectAsyncInline
          value={(targetLanguage) ? { value: targetLanguage.uid, label: targetLanguage.name } : null}
          onChange={(selection) => {
              onChange({ ...customer, defaulttargetlang: (selection) ? selection.value : '' })
          }}
          options={[]}
          label="Default target language"
          placeholder="Default target language"
          noOptionsMessage={({ inputValue }) => (inputValue) ? `No language found for '${inputValue}'` : 'No language found'}
          loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
          cacheOptions
          defaultOptions
          isClearable
          loadOptions={
              debounce((query) => fetch(`/rest/v2/language?search[name]=${query}`)
                .then((response) => response.json())
                .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
              , 250)
          }
        />

        <SelectAsyncInline
          value={(orderUsage) ? { value: orderUsage.uid, label: orderUsage.name } : null}
          onChange={(selection) => {
              onChange({ ...customer, defaultusage: (selection) ? selection.value: 0 })
          }}
          options={[]}
          label="Default airing time"
          placeholder="Default airing time"
          noOptionsMessage={({ inputValue }) => (inputValue) ? `No usage found for '${inputValue}'` : 'No usage found'}
          loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
          cacheOptions
          defaultOptions
          isClearable
          loadOptions={
              debounce((query) => fetch(`/rest/v1/orderusage?search[name]=${query}`)
                .then((response) => response.json())
                .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
              , 250)
          }
        />

        <SelectAsyncInline
          value={(audioFormat) ? { value: audioFormat.uid, label: audioFormat.name } : null}
          onChange={(selection) => {
              onChange({ ...customer, defaultaudioformat: (selection) ? selection.value : 0 })
          }}
          options={[]}
          label="Default Audio Format"
          placeholder="Default Audio Format"
          noOptionsMessage={({ inputValue }) => (inputValue) ? `No audio format found for '${inputValue}'` : 'No audio format found'}
          loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
          cacheOptions
          defaultOptions
          isClearable
          loadOptions={
              debounce((query) => fetch(`/rest/v1/audioformat?search[name]=${query}`)
                .then((response) => response.json())
                .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
              , 250)
          }
        />

        <SelectAsyncInline
          value={(orderTargetMedia) ? { value: orderTargetMedia.uid, label: orderTargetMedia.name } : null}
          onChange={(selection) => {
              onChange({ ...customer, defaultmedia: (selection) ? selection.value : 0 })
          }}
          options={[]}
          label="Default Media"
          placeholder="Default Media"
          noOptionsMessage={({ inputValue }) => (inputValue) ? `No media found for '${inputValue}'` : 'No media found'}
          loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
          cacheOptions
          defaultOptions
          isClearable
          loadOptions={
              debounce((query) => fetch(`/rest/v1/ordertargetmedia?search[name]=${query}`)
                .then((response) => response.json())
                .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
              , 250)
          }
        />
      </div>

      <div className='p-4 space-y-2'>
        { (!customer.partnerConfig.isPartner) &&
          <>
            <div>
              <SelectAsyncInline
                value={{ value: customer.partner, label: customer.partnerConfig.name }}
                onChange={(selection) => selection.value != customer.partner ?
                  changePartnerHandler(selection.value ? selection.value : 0) :
                  undefined
                }
                disabled={!currentUser.hasRole('finance')}
                options={[]}
                label="Partner"
                placeholder="Partner"
                noOptionsMessage={({ inputValue }) => inputValue ? `No partner found for '${inputValue}'` : 'No partner found'}
                loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
                cacheOptions
                defaultOptions
                loadOptions={
                    debounce((query) => fetch(`/rest/v2/partner?search[name]=${query}`)
                      .then((response) => response.json())
                      .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
                    , 250)
                }
              />
            </div>

            <div>
              <SelectInline
                labelClassName="text-xs"
                label="PO required"
                placeholder="PO required"
                value={ { value: customer.poRequired, label: (customer.poRequired == '1') ? 'Yes' : 'No' } }
                options={[
                  { value: '1', label: 'Yes' },
                  { value: '0', label: 'No' },
                ]}
                onChange={(poRequired) => { onChange({ ...customer, poRequired: poRequired.value }) } } 
              />
            </div>

            <div>
              <SelectInline
                labelClassName="text-xs"
                label="Exclude From HeyLoyalty"
                placeholder="Exclude From HeyLoyalty"
                value={ { value: customer.hl_exclude, label: (customer.hl_exclude == '1') ? 'Yes' : 'No' } }
                options={[
                  { value: '1', label: 'Yes' },
                  { value: '0', label: 'No' },
                ]}
                onChange={(hl_exclude) => { onChange({ ...customer, hl_exclude: hl_exclude.value }) } } 
              />
            </div>

            <div>
              <div><label className="font-medium block text-gray-700 hover:text-primary cursor-pointer text-xs">Credit Max</label></div>
              <div>{ customer.economicCreditMaximum !== '' ? customer.economicCreditMaximum : 'No max credit set' }</div>
            </div>

            <div>
              <SelectAsyncInline
                value={(accountingDebtorGroup) ? { value: accountingDebtorGroup.uid, label: accountingDebtorGroup.name } : null}
                onChange={(selection) => {
                    onChange({ ...customer, economicDebtorGroupHandle: (selection) ? selection.value : 0 })
                }}
                options={[]}
                label="Debtor Group"
                placeholder="Debtor Group"
                noOptionsMessage={({ inputValue }) => (inputValue) ? `No debtor group found for '${inputValue}'` : 'No debtor group found'}
                loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
                cacheOptions
                defaultOptions
                isClearable
                disabled={!currentUser.hasRole('finance')}
                loadOptions={
                    debounce((query) => fetch(`/rest/v1/accounting_debtor_group?search[name]=${query}`)
                      .then((response) => response.json())
                      .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
                    , 250)
                }
              />
            </div>

            <div>
              <SelectAsyncInline
                value={(economicLayoutHandle) ? { value: economicLayoutHandle.uid, label: economicLayoutHandle.name } : null}
                onChange={(selection) => {
                    onChange({ ...customer, economicLayoutHandle: (selection) ? selection.value : 0 })
                }}
                options={[]}
                label="Invoice Layout"
                placeholder="Invoice Layout"
                noOptionsMessage={({ inputValue }) => (inputValue) ? `No invoice layout found for '${inputValue}'` : 'No invoice layout found'}
                loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
                cacheOptions
                defaultOptions
                isClearable
                disabled={!currentUser.hasRole('finance')}
                loadOptions={
                    debounce((query) => fetch(`/rest/v1/accounting_invoice_layout?search[name]=${query}`)
                      .then((response) => response.json())
                      .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
                    , 250)
                }
              />
            </div>

            <div>
              <SelectAsyncInline
                value={(economicTermOfPaymentHandle) ? { value: economicTermOfPaymentHandle.uid, label: economicTermOfPaymentHandle.name } : null}
                onChange={(selection) => {
                    onChange({ ...customer, economicTermOfPaymentHandle: (selection) ? selection.value: 0 })
                }}
                options={[]}
                label="Payment Terms"
                placeholder="Payment Terms"
                noOptionsMessage={({ inputValue }) => (inputValue) ? `No payment terms found for '${inputValue}'` : 'No payment terms found'}
                loadingMessage={({ inputValue }) => `Searching for '${inputValue}'..`}
                cacheOptions
                defaultOptions
                isClearable
                disabled={!currentUser.hasRole('finance')}
                loadOptions={
                    debounce((query) => fetch(`/rest/v1/accounting_payment_term?search[name]=${query}`)
                      .then((response) => response.json())
                      .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
                    , 250)
                }
              />
            </div>

            {'custom_term_of_payment_days' in customer &&
              <div>
                <InputInline
                  labelClassName="text-xs"
                  type='number'
                  min='0'
                  label="Invoice due in days"
                  placeholder="Invoice due in days"
                  value={ customer.custom_term_of_payment_days }
                  onChange={({ target: { value } }) => {
                    onChange({ ...customer, custom_term_of_payment_days: value })
                  }}
                />
              </div>
            }

            <div>
              <SelectInline
                labelClassName="text-xs"
                label="Monthly Invoicing Enabled"
                placeholder="Monthly Invoicing Enabled"
                disabled={!currentUser.hasRole('finance')}
                value={ { value: customer.allowAutoInvoice, label: (customer.allowAutoInvoice == '0') ? 'Yes' : 'No' } }
                options={[
                  { value: '0', label: 'Yes' },
                  { value: '1', label: 'No' },
                ]}
                onChange={(allowAutoInvoice) => { onChange({ ...customer, allowAutoInvoice: allowAutoInvoice.value }) } } 
              />
            </div>

            <div>
              <InputInline
                labelClassName="text-xs"
                label="Submission ID"
                placeholder="Submission ID"
                disabled={customer.crmIsMaster}
                value={ customer.submissionId }
                onChange={({ target: { value } }) => {
                  onChange({ ...customer, submissionId: value })
                }}
              />
            </div>
          </>
        }
        <div>
          <SelectInline
            labelClassName="text-xs"
            label="Email the coordinator for EVERY file"
            placeholder="Email the coordinator for EVERY file"
            value={ { value: customer.force_upload_email, label: (customer.force_upload_email == '1') ? 'Yes' : 'No' } }
            options={[
              { value: '1', label: 'Yes' },
              { value: '0', label: 'No' },
            ]}
            onChange={(force_upload_email) => { onChange({ ...customer, force_upload_email: force_upload_email.value }) } } 
          />
        </div>

        <div>
          <TextArea
            rows={4}
            label="Order Note"
            defaultValue={customer.defaultordercomments}
            placeholder="Order Note"
            onBlur={({ target: { value } }) => {
              onChange({ ...customer, defaultordercomments: value })
            }}
          />
        </div>

      </div>
    </div>
  )
}

export default DefaultValues
